import { useState } from 'react'
import { Screen, SectionHeader } from '@/components/layout'
import { Button } from '@/components/primitives'
import { ConfirmDialog } from '@/components/dialog'
import { List, SettingRow } from '@/components/rows'
import { ErrorState } from '@/components/states'
import { ApiFailure, api } from '@/lib/api'
import { t } from '@/lib/copy'
import { useNavigate } from '@/lib/router'
import { useSession } from '@/lib/session'
import { useAsync } from '@/lib/useAsync'

type Pending = 'statements' | 'everything'

/**
 * Delete-everything requires an explicit confirm and returns to welcome.
 *
 * This is also the ONE place the product says that transactions are sent out
 * for analysis. Not a badge on every card — a statement about where data goes,
 * made once, where a user looks for it.
 */
export function PrivacyScreen() {
  const navigate = useNavigate()
  const { authenticated, forget } = useSession()
  const [confirming, setConfirming] = useState<Pending | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState<ApiFailure | null>(null)

  // Unauthenticated, this screen is a public page and asks the Server nothing.
  const status = useAsync(
    async () => (authenticated ? api.analysis.status().catch(() => null) : null),
    [authenticated],
  )

  /**
   * Only claim data stays local when the Server positively says generation is
   * unavailable. If the status read failed we do not know, and the honest
   * default is the disclosing sentence: over-stating where data goes is safe,
   * and under-stating it would be a false privacy claim.
   */
  const analysisOff = status.state.data?.generation.state === 'unavailable'

  const screenState = deleting ? 'deleting' : error ? 'error' : 'ready'

  const run = async (target: Pending) => {
    setDeleting(true)
    setError(null)
    try {
      if (target === 'statements') {
        await api.me.deleteStatements()
        setConfirming(null)
      } else {
        await api.me.deleteEverything()
        forget()
        navigate('welcome', {}, { replace: true })
      }
    } catch (e) {
      setError(e instanceof ApiFailure ? e : null)
      setConfirming(null)
    } finally {
      setDeleting(false)
    }
  }

  return (
    <Screen
      id="privacy"
      state={screenState}
      chrome={authenticated ? 'app' : 'standalone'}
      back={authenticated ? undefined : { screen: 'welcome' }}
    >
      <div className="stack">
        <p className="prose body-secondary">{t('privacy.body')}</p>

        <section>
          <SectionHeader title={t('privacy.section.stored')} />
          <List>
            <SettingRow label={t('privacy.stored.statements')} />
            <SettingRow label={t('privacy.stored.transactions')} />
            <SettingRow label={t('privacy.stored.analysis')} />
          </List>
        </section>

        <section>
          <SectionHeader title={t('privacy.section.analysis')} />
          <p className="prose body-secondary">
            {analysisOff ? t('privacy.analysis.disabled') : t('privacy.analysis.body')}
          </p>
        </section>

        {authenticated ? (
          <section>
            <SectionHeader title={t('privacy.section.controls')} />
            <div className="stack-sm">
              <Button variant="secondary" onClick={() => navigate('export')}>
                {t('export.title')}
              </Button>
              <Button variant="secondary" onClick={() => setConfirming('statements')} disabled={deleting}>
                {t('privacy.row.deleteStatements')}
              </Button>
              <Button variant="destructive" onClick={() => setConfirming('everything')} disabled={deleting}>
                {t('privacy.row.deleteEverything')}
              </Button>
            </div>
          </section>
        ) : null}

        {screenState === 'deleting' ? <p className="helper">{t('privacy.deleting')}</p> : null}
        {screenState === 'error' ? (
          <ErrorState titleKey="privacy.error.title" error={error ?? undefined} onRetry={() => setError(null)} />
        ) : null}
      </div>

      {confirming ? (
        <ConfirmDialog
          title={
            confirming === 'everything' ? t('privacy.delete.confirm.title') : t('privacy.row.deleteStatements')
          }
          body={t('privacy.delete.confirm.body')}
          confirmLabel={
            confirming === 'everything' ? t('privacy.delete.confirm.cta') : t('cta.delete')
          }
          busy={deleting}
          onConfirm={() => void run(confirming)}
          onCancel={() => setConfirming(null)}
        />
      ) : null}
    </Screen>
  )
}
